import EmbedYoutubeModal from "./EmbedYoutubeModal";
import MediaComponent from "@plugins/media/components/MediaComponent.vue";

import { MEDIA_TYPE } from "@plugins/media/media.constant";
import DOMUtils from "@utils/dom.utils";
import YouTubeSettings from "@plugins/media/components/YouTubeSettings.vue";
import ScrollUtils from "@utils/scroll.utils";
import i18n from "@plugins/i18n";
import richTextUtils from "../../utils/richtext.utils";
import { createApp } from "vue";
import AppInstanceUtils from "@plugins/common/service/app.instance.utils";
import VueYoutubePlugin from "@plugins/youtube";
import RichTextUtils from "@plugins/richtext/utils/richtext.utils";

export default function (editor) {
  const richTextEditor = editor.options.get("richTextEditor");
  richTextEditor.plugins["youtube"] = { renderInEditor, cleanUp, containPluginElements, openModal };

  function openModal() {
    const eventBusWatchInstances = [];
    eventBusWatchInstances.push(VueYoutubePlugin.eventBus.$on("embed-youtube", handleEmbedYoutube));
    const youtubeConfig = {};
    youtubeConfig.eventBusWatchInstances = eventBusWatchInstances;
    richTextEditor.$modal.show(EmbedYoutubeModal, youtubeConfig, {}, true);
  }

  function openSettings(settings) {
    window.tinymce.currentEditor = editor;
    settings.editorSaveCallback = editorSaveCallback;
    richTextEditor.$contentSubpage.show(YouTubeSettings, settings);
  }

  function handleEmbedYoutube(data) {
    createInlineYoutubeAttachment({
      mediaType: MEDIA_TYPE.YOUTUBE,
      youtubeSrc: data.youtubeUrl,
      type: "youtube",
    });
  }

  function createInlineYoutubeAttachment(data) {
    const placeholder = editor.dom.createHTML("div", {
      class: "inline-placeholder mceNonEditable",
      "media-type": data.mediaType,
      "data-type": data.type,
      "data-baseurl": data.youtubeSrc,
    });
    editor.insertContent(placeholder);
    renderInlineMedia(".inline-placeholder", true);
    editor.save();
  }

  function createYouTubeTag(data) {
    return DOMUtils.createElement("div", {
      class: "sls-youtube",
      "data-baseurl": data.baseurl,
      "data-start": data.start,
      "data-end": data.end,
      "data-strict": data.strict,
    });
  }

  function renderInlineMedia(selector, isCreate = false, rerender = false) {
    DOMUtils.elOf(editor.getBody(), selector).forEach((element) => {
      const richTextEditor = editor.options.get("richTextEditor");
      const propsData = {
        width: element.getAttribute("data-width"),
        height: element.getAttribute("data-height"),
        cuePoints: {
          start: parseInt(element.getAttribute("data-start")),
          end: parseInt(element.getAttribute("data-end")),
        },
        strictMode: RichTextUtils.getValueOrDefault(
          element.getAttribute("data-strict") === "true",
          false,
        ),
        youtubeSrc: element.getAttribute("data-baseurl"),
        isInline: true,
        lazyload: false,
        mediatype: "youtube",
        context: richTextEditor.richTextContext,
      };

      const mediaApp = createApp(MediaComponent, {
        ...propsData,
        ...AppInstanceUtils.generateDynamicMountProps(richTextEditor),
        onMediaLoaded: (uuid) => {
          if (rerender) {
            editor.selection.select(mediaComponent.$el);
            ScrollUtils.scrollToTopWithHeaderOffset(mediaComponent.$el, editor.getBody());
          }
          // show caret at the end of attachment container
          // after media is loaded
          window.tinymce.activeEditor.selection.select(
            window.tinymce.activeEditor
              .getBody()
              .querySelector("figure.media[data-uuid='" + uuid + "']"),
            true,
          );
          window.tinymce.activeEditor.selection.collapse(false);
          // trigger save after youtube is ready
          if (isCreate) editor.save();
        },
        onYouTubeSettings: openSettings,
      });
      AppInstanceUtils.installCommonPlugin(mediaApp);
      // Mount media component to media element, include the media tag
      const mediaComponent = mediaApp.mount(element);
      DOMUtils.replaceComponentWrapperWithComponentElement(mediaComponent);
      mediaComponent.$el.setAttribute("contenteditable", "false");
    });
  }

  //register embed youtube button icons and actions
  editor.ui.registry.addButton("youtube", {
    tooltip: i18n.t("tinymce_plugin_youtube_add_button_tooltip"),
    icon: "embed-youtube",
    onAction: function () {
      openModal();
    },
  });

  editor.addCommand("mceEmbedYouTubeDelete", (targetNode) => {
    richTextUtils.removeNodeFromEditor(editor, targetNode);
  });

  editor.on("click", (e) => {
    const target = e.target;
    if (target.closest("button.trash") && target.closest("button.trash").length !== 0) {
      const targetNode = target.closest(".youtube,sls-youtube");
      if (targetNode)
        editor.execCommand("mceEmbedYouTubeDelete", targetNode.closest(".media-component"));
    }

    // cater for cases when have undo action
    handlePluginRerender(target);
  });

  function renderInEditor(rerender = false) {
    renderInlineMedia(".sls-youtube", false, rerender);
  }

  function editorSaveCallback() {
    if (window.tinymce && window.tinymce.currentEditor) {
      window.tinymce.currentEditor.save();
      delete window.tinymce.currentEditor;
    }
  }

  function cleanUp(content) {
    const container = document.createElement("div");
    container.innerHTML = content;
    const clone = container.cloneNode(true);

    // remove placeholder
    DOMUtils.elOf(clone, ".inline-placeholder").forEach((element) => {
      element.remove();
    });

    DOMUtils.elOf(clone, "figure.youtube").forEach((element) => {
      cleanUpElement(element);
    });

    return clone.innerHTML;
  }

  function cleanUpElement(element) {
    const data = {
      start: element.getAttribute("data-cue-start"),
      end: element.getAttribute("data-cue-end"),
      strict: RichTextUtils.getValueOrDefault(
        element.getAttribute("data-strict") === "true",
        false,
      ),
      baseurl: element.getAttribute("data-baseurl"),
    };
    const youtubeTag = createYouTubeTag(data);
    element.closest(".media-component").replaceWith(youtubeTag);
  }

  function containPluginElements(container) {
    return container.querySelectorAll(".sls-youtube").length > 0;
  }

  function handlePluginRerender(target) {
    const targetNode = target.closest(".youtube,sls-youtube");
    if (!targetNode || targetNode.length === 0) return;

    const mediaComponent = targetNode.closest(".media-component");
    if (!mediaComponent || mediaComponent.length === 0 || mediaComponent.__vue__) return;

    cleanUpElement(targetNode);
    renderInEditor(true);
  }
}
